import { isOnline, logger, shortUuid } from '@tunasong/models'
import { getEntityTypeFromMimeType, type TunaPlugin } from '@tunasong/plugin-lib'
import type { TunaGetState, TunaThunkDispatch } from '@tunasong/redux'
import { features } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { openDatabase } from '@tunasong/indexeddb-storage'
import invariant from 'tiny-invariant'
import type { StoredEntityUpload } from './upload-types.js'
import { useGraphCacheStore } from '@tunasong/graph-cache'
import { blobUploadToTemporaryEntity } from './upload-util.js'

/** To guard against costly bugs that upload data ad-infinitum we set a limit on the number of files to upload per session to 1000. */
const MAX_UPLOADS_PER_SESSION = 100
// We limit the number of upload attempts to 50
const MAX_UPLOAD_ATTEMPTS = 10

// Delay between attempts (10 seconds)
const UPLOAD_RETRY_DELAY = 10000

/** Upload manager that uploads to the server asynchronously from IndexedDB */
export class UploadManager {
  #dispatch: TunaThunkDispatch
  #getState: TunaGetState
  #database: ReturnType<typeof openDatabase<StoredEntityUpload>> | null
  #uploadCount = 0
  #isInUploadLoop = false
  #uploadScheduled = false
  #pendingPromises = new Map<IDBValidKey, ((entity: Persisted<Entity>) => void) | undefined>()
  #plugins: TunaPlugin[]

  constructor({
    dbName,
    dispatch,
    getState,
    pushInterval = 1000,
    plugins,
  }: {
    dbName: string
    dispatch: TunaThunkDispatch
    getState: TunaGetState
    pushInterval?: number
    plugins: TunaPlugin[]
  }) {
    this.#dispatch = dispatch
    this.#getState = getState
    this.#database = openDatabase<StoredEntityUpload>(dbName)
    this.#plugins = plugins

    // Run upload every pushInterval milliseconds.
    setInterval(this.upload, pushInterval)
  }

  get isUploading() {
    return this.#isInUploadLoop || this.#uploadScheduled
  }

  uploadNewStoredEntity = async (props: {
    entityId?: string
    name: string
    parentId: string | null
    blob: Blob
    isPrivate?: boolean
    tags?: string[]
  }) => {
    const { isPrivate = false, blob, name, parentId, tags, entityId = shortUuid() } = props
    invariant(this.#database, `No upload database`)

    if (blob.size <= 0) {
      this.#dispatch(
        features.notifications.actions.setAlert({
          severity: 'warning',
          message: `Cannot upload an empty file: ${name}`,
        })
      )
      return
    }
    if (!blob.type) {
      this.#dispatch(
        features.notifications.actions.setAlert({
          severity: 'warning',
          message: `Cannot upload a file without a mime type: ${name}`,
        })
      )
      return
    }

    if (this.#uploadCount >= MAX_UPLOADS_PER_SESSION) {
      this.#dispatch(
        features.notifications.actions.setAlert({
          severity: 'warning',
          message: `Cannot upload more than ${MAX_UPLOADS_PER_SESSION} files per session`,
        })
      )
      return
    }

    const type = getEntityTypeFromMimeType(blob.type, this.#plugins)
    if (!type) {
      this.#dispatch(
        features.notifications.actions.setAlert({
          severity: 'warning',
          message: `Cannot upload a file with an unknown mime type: ${name}`,
        })
      )
      logger.error(`Cannot upload a file with an unknown mime type: ${name}`, { mimeType: blob.type })
      return
    }

    /** Store in the pending uploads database and schedule it for upload */
    invariant(entityId, `No entityId provided`)

    // Map the blob mime type to the entity type
    const blobUpload: StoredEntityUpload = { id: entityId, name, type, parentId, blob, isPrivate, attempts: 0, tags }
    await this.#database.set(entityId, blobUpload)

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const userId = this.#getState().user.userId!

    // Update the cache optimistically
    const { upsertCache } = useGraphCacheStore.getState()
    upsertCache(blobUploadToTemporaryEntity({ blobUpload, userId }))

    const promise = new Promise<Persisted<Entity>>(resolve => {
      this.#pendingPromises.set(entityId, resolve)
    })

    this.#uploadCount += 1
    this.#uploadScheduled = true

    // trigger an upload async
    this.upload().catch(e => {
      logger.error('Error uploading', e)
    })

    return promise
  }

  getPendingUploadBlobURL = async (id: string) => {
    const item = await this.#database?.get(id)
    if (!item) {
      return
    }
    return URL.createObjectURL(item.blob)
  }

  // the private upload loop

  private upload = async () => {
    if (!this.#database || this.#isInUploadLoop || !isOnline()) {
      return
    }

    this.#isInUploadLoop = true

    for (const key of await this.#database.keys()) {
      const val = await this.#database.get(key)
      if (!val) {
        continue
      }

      const { id, name, parentId, blob, isPrivate, tags, type, lastAttempt } = val

      // Wait at least UPLOAD_RETRY_DELAY before retrying
      if (lastAttempt && Date.now() - lastAttempt.getTime() < UPLOAD_RETRY_DELAY) {
        await new Promise(resolve => setTimeout(resolve, UPLOAD_RETRY_DELAY))
        continue
      }

      try {
        /** We need to remove the key before uploading to avoid a race */
        await this.#database.delete(key)

        const entity = await this.#dispatch(
          features.storage.thunks.uploadStoredEntity({
            id,
            name,
            type,
            parentId,
            blob,
            message: `Uploading ${name}...`,
            tags,
            isPrivate,
          })
        ).unwrap()

        const resolve = this.#pendingPromises.get(key)
        if (!resolve) {
          continue
        }
        resolve(entity)
        this.#pendingPromises.delete(key)
      } catch (e) {
        /** Re-queue the entity on error */
        logger.warn(`Error uploading ${name}. Rescheduling upload.`, e)
        const attempts = val.attempts + 1
        if (attempts > MAX_UPLOAD_ATTEMPTS) {
          logger.error(`Failed to upload ${name} after ${MAX_UPLOAD_ATTEMPTS} attempts. Giving up.`)
          this.#pendingPromises.delete(key)
          continue
        }
        this.#database.set(key, {
          ...val,
          lastAttempt: new Date(),
          attempts,
        })
      }
    }
    this.#isInUploadLoop = false
    this.#uploadScheduled = false
  }
}
